import { useEffect, useRef, useState } from 'react';
import { hayMasALaDerecha, scrollParaMostrar } from '../lib/desborde';
import { inicioFramework, solapasFramework } from '../lib/framework';

type Props = {
  /**
   * Histórico: 'oscuro' | 'compacto' se aceptan por compatibilidad y se ven igual
   * que 'barra' (barra superior del sitio del framework).
   */
  variante?: 'barra' | 'oscuro' | 'compacto';
  /** Sello a la derecha de la barra (Courier, estilo «v1.4 · borrador»). */
  subtitulo?: string;
  /** Título accesible del h1 (oculto visualmente). */
  titulo?: string;
  /** false cuando la página ya tiene su propio h1 visible (landing). */
  conTitulo?: boolean;
  /** Solapas del sitio del framework. */
  conSolapas?: boolean;
  /** Solapa de la página actual (`id` de `solapasFramework()`). */
  actual?: string;
  /** Destino de la marca sin solapas (p. ej. inicio del Radar en su dominio). */
  hrefMarca?: string;
  className?: string;
};

/**
 * Barra de marca «Innovación con IA · CESSI», igual a la del sitio del framework.
 * El PNG es el lockup oficial del manual; el nombre va en HTML (traducible).
 */
export function BrandLockup({
  variante = 'barra',
  subtitulo,
  titulo = 'Innovación con IA',
  conTitulo = true,
  conSolapas = false,
  actual = 'autoevaluacion',
  hrefMarca,
  className,
}: Props) {
  const clases = ['brand-lockup', `brand-lockup--${variante}`, className]
    .filter(Boolean)
    .join(' ');
  const solapasRef = useRef<HTMLElement>(null);
  const [hayMas, setHayMas] = useState(false);

  useEffect(() => {
    const nav = solapasRef.current;
    if (!nav) return;

    const actual = nav.querySelector<HTMLElement>('[aria-current="page"]');
    if (actual) {
      const rNav = nav.getBoundingClientRect();
      const rActual = actual.getBoundingClientRect();
      const destino = scrollParaMostrar(
        rActual.left - rNav.left + nav.scrollLeft,
        rActual.width,
        nav.scrollLeft,
        nav.clientWidth,
      );
      if (destino !== null) {
        const reducido =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        nav.scrollTo({ left: destino, behavior: reducido ? 'auto' : 'smooth' });
      }
    }

    let frame = 0;
    const medir = () => {
      frame = 0;
      setHayMas(hayMasALaDerecha(nav.scrollLeft, nav.clientWidth, nav.scrollWidth));
    };
    const programar = () => {
      if (!frame) frame = window.requestAnimationFrame(medir);
    };
    medir();
    nav.addEventListener('scroll', programar, { passive: true });
    window.addEventListener('resize', programar, { passive: true });
    return () => {
      nav.removeEventListener('scroll', programar);
      window.removeEventListener('resize', programar);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [conSolapas]);

  const marca = (
    <>
      <img
        className="brand-lockup__isotipo"
        src={`${import.meta.env.BASE_URL}marca/lockup-completo.png`}
        alt=""
        width={41}
        height={34}
        decoding="async"
      />
      <span className="brand-lockup__nombre">Innovación con IA · CESSI</span>
    </>
  );

  return (
    <header className={clases}>
      <div className="brand-lockup__barra">
        {conSolapas || hrefMarca ? (
          <a className="brand-lockup__marca" href={conSolapas ? inicioFramework() : hrefMarca}>
            {marca}
          </a>
        ) : (
          <div className="brand-lockup__marca">{marca}</div>
        )}
        {conSolapas && (
          <nav
            ref={solapasRef}
            className={`brand-lockup__solapas${hayMas ? ' brand-lockup__solapas--hay-mas' : ''}`}
            aria-label="Principal"
          >
            {solapasFramework().map((s) => (
              <a
                key={s.id}
                href={s.href}
                aria-current={s.id === actual ? 'page' : undefined}
              >
                {s.corta ? (
                  <>
                    <span className="brand-lockup__solapa-larga">{s.etiqueta}</span>
                    <span className="brand-lockup__solapa-corta">{s.corta}</span>
                  </>
                ) : (
                  s.etiqueta
                )}
              </a>
            ))}
          </nav>
        )}
        {subtitulo && <p className="brand-lockup__sub">{subtitulo}</p>}
      </div>
      {conTitulo && <h1 className="brand-lockup__titulo visually-hidden">{titulo}</h1>}
    </header>
  );
}
